// import config from '../../../../config';

import { getAttributionProperties } from '../../../../store/types/attribution_properties';
import { customProxySettings } from '../../api/proxy/proxySettings';
import { type MakeRequest, type RequestResult, type Settings } from '../../structs';

/** Make the request */
export async function makeRequest<T>({
    req,
    token,
    settings,
    prefetch,
    apiSettingsType = 'api',
}: MakeRequest): RequestResult<T> {
    let requestSettings = settings;

    const addCustomSettings = apiSettingsType === 'daas' || apiSettingsType === 'daasV2' || apiSettingsType === 'auth';

    if (addCustomSettings) {
        requestSettings = { ...settings, ...(customProxySettings[apiSettingsType] as Settings) };
    }

    if (prefetch) {
        token = await prefetch();
    }
    if (token) {
        const tokenString = typeof token === 'string' ? token : token.access_token;
        if (tokenString && requestSettings?.credentials !== 'omit') {
            req.headers.append('Authorization', `Bearer ${tokenString}`);
        }
    }

    const deviceId = window.localStorage.getItem('amplitudeDeviceId');
    if (deviceId && apiSettingsType !== 'auth') {
        req.headers.append('Amplitude-DeviceId', deviceId);
    }

    const sessionId = window.localStorage.getItem('amplitudeSessionId');
    if (sessionId && apiSettingsType !== 'auth') {
        req.headers.append('Amplitude-SessionId', sessionId);
    }

    const attributionProperties = getAttributionProperties();
    if (attributionProperties && apiSettingsType !== 'auth' && apiSettingsType !== 'marketplace') {
        req.headers.append('Segment-AttributionData', JSON.stringify(attributionProperties));
    }

    req.headers.append('Referer', document.referrer);

    // Remove headers passed via `ignoreHeaders`
    if (requestSettings?.ignoreHeaders) {
        requestSettings?.ignoreHeaders.forEach((ignoreHeader) => {
            if (req.headers.has(ignoreHeader)) {
                req.headers.delete(ignoreHeader);
            }
        });
    }

    try {
        const resp = await fetch(req, {
            signal: requestSettings?.signal,
            credentials: requestSettings?.credentials || 'include',
            referrerPolicy: 'no-referrer-when-downgrade',
        });
        if (!resp.ok) {
            const error = await resp.json();
            return {
                ok: false,
                status: {
                    code: resp.status,
                    text: resp.statusText,
                },
                error: error.error,
                headers: resp.headers,
                fields: error.fields,
            };
        }

        try {
            const result: T = await resp.json();
            return {
                ok: true,
                value: result,
                headers: resp.headers,
                status: {
                    code: resp.status,
                    text: resp.statusText,
                },
            };
        } catch (e) {
            return {
                ok: false,
                status: {
                    code: 0,
                    text: 'Error',
                },
                error: {
                    status: 0,
                    title: 'Error parsing response',
                    detail: 'API returned a non-JSON response',
                    code: '0.parse_error',
                    raw: e,
                },
                headers: resp.headers,
            };
        }
    } catch (e) {
        return {
            ok: false,
            status: {
                code: 0,
                text: 'Error',
            },
            error: {
                status: 0,
                title: 'Unable to reach server',
                detail: 'There was an error from the server.',
                code: '0.network_error',
                raw: e,
            },
            headers: new Headers(),
        };
    }
}
